import React, { useEffect } from 'react'
import { useState } from 'react'
import { H3 } from '../../../layouts/TextStyles'
import { Box, LinearProgress, Tab, Tabs, Typography } from '@mui/material'
import styled from '@emotion/styled'
import { useIntl } from 'react-intl'
import { useSearchParams } from 'react-router-dom'
import AnalysisTable from './components/AnalysisTable'
import { examAnalysisApiSlice } from '../../../features/curator/examAnalysisSlice'
import { CourseExamState } from '../../../features/types'
import CircularProgressWithLabel from '../../../components/muiComponents/circularProgressWithLabel'
import { capitalize } from '../../../utils/functions'

const AnalysisPage = () => {
    const [searchParams, setSearchParams] = useSearchParams('')
    const { formatMessage, locale } = useIntl()
    const [fetchListOfExams, { data: listOfExams }] = examAnalysisApiSlice.useLazyFetchListOfExamsQuery()
    const [fetchExamParticipation, { data: examParticipation, isFetching: isFetchingParticipation }] = examAnalysisApiSlice.useLazyFetchExamParticipationQuery()
    const [fetchExamScoreRange, { data: examScoreRange, isFetching: isFetchingScoreRange }] = examAnalysisApiSlice.useLazyFetchExamScoreRangeQuery()
    const [fetchStudentAnalysis, { data: studentAnalysis, isFetching: isFetchingStudentAnalysis }] = examAnalysisApiSlice.useLazyFetchStudentAnalysisQuery()
    const [examId, setExamId] = useState<number>(0)
    const [page, setPage] = React.useState(
        Number(searchParams.get('page')) || 1,
    )
    const [rowsPerPage, setRowsPerPage] = React.useState(
        Number(searchParams.get('size')) || 5,
    )

    useEffect(() => {
        fetchListOfExams()
    }, [])

    useEffect(() => {
        if (listOfExams?.length > 0 && examId === 0) {
            handleChangeExamId(listOfExams[0].id)
        }
    }, [listOfExams])

    useEffect(() => {
        if (examId > 0) {
            getStudents(examId)
        }
    }, [examId, page, rowsPerPage])

    const handleChangeExamId = (id: number) => {
        setExamId(id)
        fetchExamParticipation({ examId: id })
        fetchExamScoreRange({ examId: id })
        getStudents(id)
    }


    const getStudents = (examId: number) => {
        fetchStudentAnalysis({
            examId,
            page: page - 1,
            size: rowsPerPage,
        })
        setQueryParams()
    }

    const setQueryParams = () => {
        const params = {
            page: page.toString(),
            size: rowsPerPage.toString(),
        }
        setSearchParams(params)
    }

    return (
        <Wrapper>
            <Content>
                <Header>
                    <Title>
                        <H3>{formatMessage({ id: 'examAnalysis' })}</H3>
                    </Title>
                    <Tabs sx={styles.buttonGroup} variant="scrollable" scrollButtons="auto">
                        {listOfExams?.map((exam: CourseExamState) => (
                            <Tab key={exam.id} onClick={() => handleChangeExamId(exam.id)} sx={styles.button(examId === exam.id)}
                                label={exam[`title${capitalize(locale)}`]} />
                        ))}
                    </Tabs>
                </Header>
                {isFetchingParticipation || isFetchingScoreRange ? (
                    <LinearProgress />
                ) : (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
                        <ProgressCard>
                            <CircularProgressWithLabel value={examParticipation?.passedExamPercent || 0} size={64} color={'secondary'} variant="determinate" />
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                <Typography variant="body1">
                                    {formatMessage({ id: 'passed' })} :
                                    <span style={{ fontWeight: 600, color: '#5094FF' }}>{examParticipation?.passedStudentCount} {' '} {formatMessage({ id: 'student' })}</span>
                                </Typography>
                                <Typography variant="body1">
                                    {formatMessage({ id: 'unpassed' })} :
                                    <span style={{ fontWeight: 600, color: '#858D9D' }}>{examParticipation?.unPassedStudentCount} {' '} {formatMessage({ id: 'student' })}</span>
                                </Typography>
                            </Box>
                        </ProgressCard>
                        <ProgressCard>
                            <CircularProgressWithLabel value={examParticipation?.averagePercent || 0} size={64} color={'secondary'} variant="determinate" />
                            <Typography variant="body1">
                                {formatMessage({ id: 'averagePoint' })} :
                                <span style={{ fontWeight: 600, color: '#5094FF' }}>{examParticipation?.averageScore} балл</span>
                            </Typography>
                        </ProgressCard>
                        <StatisticsCard>
                            <Typography variant="body1" sx={styles.statisticsText}>
                                0-19 балл
                            </Typography>
                            <span style={{ fontWeight: 600, color: '#1B998B' }}>{examScoreRange?.firstCol} {' '} {formatMessage({ id: 'student' })}</span>
                        </StatisticsCard>
                        <StatisticsCard>
                            <Typography variant="body1" sx={styles.statisticsText}>
                                20-29 балл
                            </Typography>
                            <span style={{ fontWeight: 600, color: '#1B998B' }}>{examScoreRange?.secondCol} {' '} {formatMessage({ id: 'student' })}</span>
                        </StatisticsCard>
                        <StatisticsCard>
                            <Typography variant="body1" sx={styles.statisticsText}>
                                30-39 балл
                            </Typography>
                            <span style={{ fontWeight: 600, color: '#1B998B' }}>{examScoreRange?.thirdCol} {' '} {formatMessage({ id: 'student' })}</span>
                        </StatisticsCard>
                        <StatisticsCard>
                            <Typography variant="body1" sx={styles.statisticsText}>
                                40-50 балл
                            </Typography>
                            <span style={{ fontWeight: 600, color: '#1B998B' }}>{examScoreRange?.fourthCol} {' '} {formatMessage({ id: 'student' })}</span>
                        </StatisticsCard>
                    </Box>
                )}
                {isFetchingStudentAnalysis ? (
                    <LinearProgress />
                ) : (
                    studentAnalysis && (
                        <AnalysisTable
                            studentsData={studentAnalysis?.data}
                            pageNumber={studentAnalysis.pageNumber}
                            totalCount={studentAnalysis.totalCount}
                            page={page}
                            rowsPerPage={rowsPerPage}
                            isFetching={isFetchingStudentAnalysis}
                            setPage={setPage}
                            setRowsPerPage={setRowsPerPage}
                        />
                    )
                )}
            </Content>
        </Wrapper >
    )
}

export default AnalysisPage

const Wrapper = styled.div`
    margin: 10px;
    border-radius: 16px;
`
const Title = styled.div`
    margin-bottom: 20px;
    text-align: left;
    display: flex;
    align-items: center;
`

const Content = styled.div`
    background-color: #fff;
    border-radius: 16px;
    padding: 20px;
    min-height: 80vh;
`

const ProgressCard = styled.div`
    border-radius: 12px;
    padding: 16px;
    border: 1px solid #4D60D7;
    display: flex;
    gap: 12px;
    align-items: center;
`

const StatisticsCard = styled.div`
    border-radius: 12px;
    padding:  18px 22px;
    border: 1px solid #BACFF2;
    justify-content: center;
    text-align: center;
`

const Header = styled.header`
`

const styles = {
    filterBox: {
        display: 'flex',
        justifyContent: 'space-between',
        marginBottom: 2,
        borderBottom: '1px solid #b4b4b4',
    },
    statisticsText: {
        padding: '4px 12px',
        borderRadius: '12px',
        backgroundColor: '#E0E2E7',
        fontSize: '14px',
        color: '#4D5464',
        fontWeight: 600,
        marginBottom: '12px',
        overflowX: 'auto',
    },
    buttonGroup: {
        my: 3,
        display: 'flex',
    },
    button: (selected: boolean) => ({
        maxWidth: '100%',
        borderRadius: '0',
        borderTop: 'none',
        borderBottom: selected ? '1px solid #E62D95' : '1px solid #b4b4b4',
        borderLeft: 'none',
        backgroundColor: selected ? '#FDECF1' : 'transparent',
        borderRight: 'none',
        color: selected ? '#E62D95' : '#666666',
        '&:hover': {
            borderTop: 'none',
            borderBottom: '1px solid #E62D95',
            borderLeft: 'none',
            borderRight: 'none',
            color: '#E62D95',
            backgroundColor: 'transparent',
        },
    }),
}